import groq from "groq"

import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { groqQueryOptions } from "../../shared/utils/groqQueryOptions"

/**
 * Retrieves bundles from Sanity.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.context - The context object.
 * @param {number} params.offset - The offset.
 * @param {number} params.limit - The limit.
 * @param {string} params.sort - The sort.
 * @param {string} params.lang - The language.
 * @param {boolean} params.cdn - The CDN flag.
 * @param {boolean} params.sortWithinLimits - The sort within limits flag.
 * @param {string} options.searchString - The search string.
 * @returns {Object} - The bundles.
 */
async function getBundlesFromSanity({ context, offset, limit, sort, lang, cdn, sortWithinLimits, searchString }) {
  const config = AppSettings.sanityConfigByLang(lang)
  const client = context.sanityClient
  client.config({ ...config, useCdn: cdn })

  const { pipesAndLimits, draftsFilter } = groqQueryOptions({
    sort,
    offset,
    limit,
    sortWithinLimits,
    includeDrafts: true,
  })

  const totalCountQuery = groq`count(*[_type=="bundle" ${draftsFilter}])`
  const bundlesQuery = groq`*[_type=="bundle" ${draftsFilter}]${pipesAndLimits}{
        "id": _id,
         title,
         image,
  }`

  const [totalCount, bundleItems] = await Promise.all([
    client.fetch(totalCountQuery, { term: searchString }),
    client.fetch(bundlesQuery, { term: searchString }),
  ])
  const bundles = {
    total: totalCount,
    items: bundleItems,
  }
  return bundles
}
/**
 * Retrieves bundles based on the provided arguments.
 *
 * @param {Object} parent - The parent object.
 * @param {Object} args - The arguments object.
 * @param {Object} context - The context object.
 * @param {Object} info - The info object.
 * @returns {Object} - The bundles and related information.
 */
export const queryBundles = {
  async bundles(parent, args, context, info) {
    const { offset = 0, limit = 25, sort, lang = AppSettings.defaultLocale(), cdn = true } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ARTICLES,
      operation: Operations.READ,
    })

    const bundles = await getBundlesFromSanity({
      context,
      offset,
      limit,
      sort,
      lang,
      cdn,
    })

    return {
      ...bundles,
      offset,
      limit,
      count: bundles?.items?.length,
    }
  },
}
